/**
 * Carrier shipment lifecycle — the SINGLE source for these values.
 * `@stopal/db` builds the `carrier_status` Postgres enum FROM this array
 * (`pgEnum('carrier_status', CARRIER_STATUSES)`), so the two can't drift and no
 * lockstep test is needed. This leaf package is the shared home because the ops
 * carrier desk can import it but can't import `@stopal/db`.
 *
 * **This is another status axis, not a widening of `order_status`.** It follows
 * the `StopStatus` precedent exactly: a Stop's status is not an Order Event
 * status because the event taxonomy carries `confirmed` and `scheduled` that no
 * stop holds — and a carrier shipment likewise holds `in_transit` and
 * `out_for_delivery`, which no fleet trip ever holds. Render a shipment through
 * `CARRIER_STATUS_META`, never through the order-event preset map.
 *
 * **The value set is a one-way door.** A Postgres enum value cannot be dropped;
 * retiring one costs a type-recreate migration. Settle a value here rather than
 * discovering it later.
 */
export const CARRIER_STATUSES = [
  'booked',
  'tendered',
  'picked_up',
  'in_transit',
  'out_for_delivery',
  'delivered',
  'delayed',
  'exception',
] as const

export type CarrierStatus = (typeof CARRIER_STATUSES)[number]

/**
 * Where each status sits on the happy path — `null` means it is off-path.
 *
 * ONE structure carrying three facts: which
 * values exist, which are off-path, and what order the on-path ones run in.
 *
 * A separate `CARRIER_STATUS_SEQUENCE` array beside an exhaustive classifier
 * switch, with a test asserting the two agreed, is the shape
 * `20-architecture.md` calls a smell — "a lockstep test that asserts two
 * copies match is a smell, not a fix; delete a copy" — and it is worse than
 * merely redundant: a test that compared the two SORTED would pin membership
 * only, while the ORDERING (the sole information the second list carried)
 * could be changed silently. It is a drift guard that cannot see the drift it
 * was written for.
 *
 * `Record<CarrierStatus, …>` keeps what the switch was for: a value added to
 * the taxonomy fails the build here until somebody decides where it ranks, or
 * that it does not rank at all.
 */
export const CARRIER_STATUS_RANK: Record<CarrierStatus, number | null> = {
  booked: 0,
  tendered: 1,
  picked_up: 2,
  in_transit: 3,
  out_for_delivery: 4,
  delivered: 5,
  delayed: null,
  exception: null,
}

/**
 * Is this an off-path state — the shipment is not progressing normally?
 *
 * Reads the rank rather than testing the two names, so it cannot disagree with
 * the ordering. The complement spelling (`status !== 'delayed' && …`) would be
 * worse than a hand-written list: a value added to the enum is by construction
 * absent from the thing being excluded, so it would be silently classified as
 * PROGRESSING NORMALLY — the answer that puts a stuck shipment on the board
 * looking healthy.
 */
export function isCarrierStatusOffPath(status: CarrierStatus): boolean {
  return CARRIER_STATUS_RANK[status] === null
}

/**
 * The off-path statuses as a value, so SQL and board filters can NAME them
 * (`current_status in (…)`) instead of calling a predicate per row.
 *
 * DERIVED from the rank registry, never listed beside it. No SQL reads it yet —
 * the board that will is a later child — so read this as the shape provided
 * for it, not as a claim that something already consumes it.
 */
export const OFF_PATH_CARRIER_STATUSES: readonly CarrierStatus[] =
  CARRIER_STATUSES.filter(isCarrierStatusOffPath)

/** The complement, same single source. */
export const ON_PATH_CARRIER_STATUSES: readonly CarrierStatus[] =
  CARRIER_STATUSES.filter((status) => !isCarrierStatusOffPath(status))

/**
 * The end of the line. A delivered shipment accepts NO further transition —
 * not even to `delayed`, which would otherwise be the back door around the
 * forward-only rule: `delivered → delayed → picked_up` is two legal-looking
 * hops that together undo a delivery.
 */
export function isCarrierStatusTerminal(status: CarrierStatus): boolean {
  return status === 'delivered'
}

/**
 * The terminal statuses as a value, so SQL can NAME them — the ops desk splits
 * its queue on exactly this line: everything still in flight above, oldest
 * first, and everything finished below, newest first.
 *
 * DERIVED from the predicate rather than listed beside it, for the reason
 * `OFF_PATH_CARRIER_STATUSES` gives one function up: a status added to the
 * taxonomy is by construction absent from a hand-written list, so it would be
 * silently classified as STILL IN FLIGHT and sit at the top of an ops queue
 * forever.
 */
export const TERMINAL_CARRIER_STATUSES: readonly CarrierStatus[] =
  CARRIER_STATUSES.filter(isCarrierStatusTerminal)

/** The status a shipment is born in. Nothing ever transitions back TO it. */
export const INITIAL_CARRIER_STATUS: CarrierStatus = 'booked'

/**
 * Has the job been COMMITTED to a carrier — i.e. is it past the point where
 * refusing it is still free?
 *
 * A `Record`, not `status !== INITIAL_CARRIER_STATUS`. The complement spelling
 * is the shape `20-architecture.md` singles out — "deriving a set as the
 * COMPLEMENT of an unguarded one is worse than the hand-typed copy" — and the
 * direction it fails in is the expensive one: a status added to the taxonomy is
 * by construction not `booked`, so it would be classified as ALREADY COMMITTED,
 * and the consent gate (`consent-gate.ts`, read by
 * `carrierShipmentService.recordTransition`) would wave every hop out of it
 * through without asking. This way a new value fails the build until somebody
 * decides which side of the line it sits on.
 *
 * The rank registry cannot answer this. `delayed` and `exception` rank `null`
 * and are committed; a hypothetical pre-tender `on_hold` would be uncommitted
 * whatever rank it were given. Ranking is about ORDER, this is about whether
 * the goods are somebody else's problem yet.
 */
export const CARRIER_STATUS_COMMITS_THE_JOB: Record<CarrierStatus, boolean> = {
  booked: false,
  tendered: true,
  picked_up: true,
  in_transit: true,
  out_for_delivery: true,
  delivered: true,
  delayed: true,
  exception: true,
}

/** Is the job already in a carrier's hands, by the registry above? */
export function isCarrierJobCommitted(status: CarrierStatus): boolean {
  return CARRIER_STATUS_COMMITS_THE_JOB[status]
}

/**
 * The committed statuses as a value, so SQL can NAME them — the customer's
 * dispatch cutoff asks this of a carrier leg the way it asks `stopDispatched()`
 * of a fleet one. DERIVED from the registry, never listed beside it.
 */
export const COMMITTED_CARRIER_STATUSES: readonly CarrierStatus[] =
  CARRIER_STATUSES.filter(isCarrierJobCommitted)

/**
 * Has the carrier physically TAKEN the pallet? True from `picked_up` onward.
 *
 * A rank comparison rather than `=== 'picked_up'`: forward skips are legal, so
 * `tendered → delivered` in one hop never passes through `picked_up`, and a
 * collection keyed on the name would be missed exactly then. The off-path
 * statuses rank `null` and answer false — a `delayed` report says nothing new
 * about collection, and whatever the shipment held before it already did.
 */
export function hasCarrierCollected(status: CarrierStatus): boolean {
  const rank = CARRIER_STATUS_RANK[status]
  return rank !== null && rank >= carrierStatusRank('picked_up')
}

/**
 * The collected statuses as a value, so SQL can NAME them. DERIVED from the
 * predicate, never listed beside it — there are FOUR of them, not one, and a
 * reader that enumerates by hand is how `in_transit` and `out_for_delivery`
 * came to be forgotten twice.
 *
 * Read it against the append-only `carrier_shipment_update` log rather than the
 * shipment row: `current_status` holds one value and the off-path statuses rank
 * `null`, so neither it nor the two `*_at` stamps can answer "did a carrier
 * EVER have this". See `wasEverCollected`.
 */
export const COLLECTED_CARRIER_STATUSES: readonly CarrierStatus[] =
  CARRIER_STATUSES.filter(hasCarrierCollected)

/**
 * What a carrier report makes of the Order Event's own status, or `null` for
 * a report that moves nothing.
 *
 * The Order Event's status is coarser than the shipment's: `in_progress` once
 * the carrier holds the pallet, `completed` once it is delivered. `tendered`
 * and the two off-path reports leave it alone — the event is already open, and
 * the Order Event status has no word for "late". A `Record` so a carrier status
 * added to the taxonomy fails the build here until somebody decides.
 */
export const CARRIER_STATUS_ORDER_EVENT_STATUS: Record<
  CarrierStatus,
  'in_progress' | 'completed' | null
> = {
  booked: null,
  tendered: null,
  picked_up: 'in_progress',
  in_transit: 'in_progress',
  out_for_delivery: 'in_progress',
  delivered: 'completed',
  delayed: null,
  exception: null,
}

/**
 * The earliest moment a carrier report can plausibly describe.
 *
 * A floor rather than a business rule: a bare date accepts year -1, and the
 * update log is append-only, so a fat-fingered year is permanent.
 *
 * **Here rather than beside the input schema because BOTH ends need it.** The
 * API refuses an out-of-window date, and the ops desk's date controls carry the
 * same bound as `min`/`max` — which is the only thing that turns that refusal
 * into something an operator can act on. A tRPC input failure serialises as an
 * issues ARRAY, so its message is not an authored sentence and every surface
 * correctly replaces it with generic copy; the specific wording only ever
 * reaches a person if the control refuses first.
 */
export const CARRIER_REPORT_EARLIEST_ISO = '2020-01-01T00:00:00.000Z'

/** A day of slack for clock skew and a carrier reporting from another zone. */
export const CARRIER_REPORT_FUTURE_SLACK_MS = 24 * 60 * 60 * 1000

/**
 * May a shipment move from `from` to `to`?
 *
 * The one expression of the rule, so the service and any surface later offering
 * a status picker refuse the same set. Today only the service reads it. Rules:
 *
 * 1. **Nothing leaves a terminal status.** See `isCarrierStatusTerminal`.
 * 2. **A no-op is refused, not ignored.** Recording the status a shipment
 *    already holds is an operator mistake worth saying out loud; swallowing it
 *    writes a log row asserting a change that did not happen.
 * 3. **On-path moves forward only.** Skipping ahead is allowed — ops often
 *    learn of a pickup and a departure in one phone call — but never back.
 * 4. **Off-path is reachable from anything still in flight** — including from
 *    the other off-path status, so a delay that turns out to be damage is one
 *    hop — **and returns to any on-path status except `booked`.** The position
 *    held before the exception is deliberately not stored: a shipment that went
 *    `in_transit → delayed` has no recorded rank to resume from, and refusing
 *    the resume would strand it. `booked` is excluded because it means "we have
 *    not tendered this yet", which is never true again once anything has
 *    happened. The cost of not storing it is real and accepted: `delayed →
 *    tendered` is a legal two-hop rewind of a move `in_transit → tendered`
 *    refuses directly.
 */
export function canTransitionCarrierStatus(
  from: CarrierStatus,
  to: CarrierStatus,
): boolean {
  if (isCarrierStatusTerminal(from)) return false
  if (from === to) return false
  if (isCarrierStatusOffPath(to)) return true
  if (to === INITIAL_CARRIER_STATUS) return false
  if (isCarrierStatusOffPath(from)) return true
  return carrierStatusRank(to) > carrierStatusRank(from)
}

/**
 * Every status this shipment may legally be moved to, in taxonomy order.
 *
 * The ops desk's status picker reads this, so the control offers exactly the
 * set the service accepts. That is presentation only — the picker narrowing
 * the choice is a convenience, and `recordTransition` re-asks the same rule in
 * its `WHERE`, because the row can move under a form left open on a desk.
 *
 * Derived from `canTransitionCarrierStatus` rather than reasoning about the
 * ranks a second time: a picker with its own copy of the rule is how a surface
 * comes to offer a hop the server refuses, which reads to an operator as the
 * screen being broken.
 */
export function carrierStatusTargetsFrom(from: CarrierStatus): CarrierStatus[] {
  return CARRIER_STATUSES.filter((to) => canTransitionCarrierStatus(from, to))
}

/**
 * Position on the happy path, for a status already known to be on it.
 *
 * The `?? -1` is unreachable — every caller is behind an off-path guard — and
 * is a definite number rather than a sentinel that compares as "before
 * everything", which is what `indexOf` would hand back here.
 */
function carrierStatusRank(status: CarrierStatus): number {
  return CARRIER_STATUS_RANK[status] ?? -1
}

export interface CarrierStatusMeta {
  /** What the ops desk calls this state. */
  label: string
  /** One line saying what it asserts about the box. */
  description: string
}

/**
 * The registry every surface should read. Rendering a board off the same record
 * the transition service keys by is what stops a column existing for a status
 * nothing can reach — the failure a per-surface duplicate map always produces.
 * Nothing renders it yet; the ops desk is a later child.
 *
 * `Record<CarrierStatus, …>` is load-bearing: a value added to the array above
 * fails the build here until someone decides what it is called.
 *
 * **No copy here reaches a customer.** These are the operator lexicon, the way
 * `ORDER_EVENT_TYPE_META.opsLabel` is; anything a customer reads about a
 * carrier move comes from the `customer_message` catalog.
 */
export const CARRIER_STATUS_META: Record<CarrierStatus, CarrierStatusMeta> = {
  booked: {
    label: 'Booked',
    description:
      'The customer has paid for the move. No carrier has been given the job yet.',
  },
  tendered: {
    label: 'Tendered to carrier',
    description:
      'The job has been handed to a trucking company, which has accepted it.',
  },
  picked_up: {
    label: 'Picked up',
    description: 'The carrier has collected the pallet from the origin.',
  },
  in_transit: {
    label: 'In transit',
    description: 'The pallet is moving between terminals.',
  },
  out_for_delivery: {
    label: 'Out for delivery',
    description: 'On the truck for its final run to the destination.',
  },
  delivered: {
    label: 'Delivered',
    description:
      'The pallet reached the destination. This is the end of the line — nothing follows it.',
  },
  delayed: {
    label: 'Delayed',
    description:
      'Still on its way, but behind the promised date. Resume it onto whatever the carrier reports next.',
  },
  exception: {
    label: 'Exception',
    description:
      'Something is wrong that a person has to resolve — damage, a refused delivery, a lost pallet.',
  },
}

/**
 * What a carrier shipment log entry records.
 *
 * Here rather than in `@stopal/db` — where it was first declared — for the
 * reason every sibling in this feature is here: the ops carrier desk has to
 * label a log row's kind and cannot import the database package. `@stopal/db`
 * derives `pgEnum('carrier_shipment_update_kind', …)` from this array.
 *
 * The table answers "what happened to this shipment" and both kinds belong in
 * it: a status move, and an edit to what the carrier told us. Without the
 * discriminator the two are indistinguishable, because a detail edit carries
 * the status the shipment already held.
 */
export const CARRIER_SHIPMENT_UPDATE_KINDS = ['transition', 'detail'] as const

export type CarrierShipmentUpdateKind =
  (typeof CARRIER_SHIPMENT_UPDATE_KINDS)[number]
